import { map, switchMap, take, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { Paths } from '@config/paths.config';

import { AuthModeValues } from '@config/values/order.values';
import {
  isPatMockMode,
  isPatMockPayAtTableUrl,
  resolvePatMockCheckId,
} from '@config/values/pat.values';

import { ModalsService } from '@core/services/modals/modals.service';
import { AuthFirebaseService } from '@core/services/api/auth-firebase/auth-firebase.service';

import { CartStoreSelector } from '@common/selectors/cart.selector';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { AuthStoreDispatcher } from '@common/dispatchers/authentication.dispatcher';
import { AuthenticationModalComponent } from '@shared/modals/authentication-modal/authentication-modal.component';
import { UnverifiedUserEmailAlertModalData } from '@shared/modals/unverified-user-email-alert-modal/unverified-user-email-alert-modal.component';
import { UntypedModalIO } from '@core/services/modals/modals-util.type';
import { Store } from '@ngrx/store';
import { SELECT_IS_USER_EMAIL_VERIFICATION_PLUGIN_ACTIVE } from '@store/authentication/user-email-verification.selection';
import { AuthVerificationEmailService } from '@core/services/auth/auth-verification-email.service';
import { AnalyticsService } from '@core/services/app/analytics.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private cartSelector: CartStoreSelector,
    private authSelector: AuthStoreSelector,
    private authDispatcher: AuthStoreDispatcher,
    private readonly modalsService: ModalsService<[UnverifiedUserEmailAlertModalData]>,
    private readonly authFirebaseService: AuthFirebaseService,
    private readonly store: Store,
    private readonly authVerificationEmailService: AuthVerificationEmailService,
    private readonly analyticsService: AnalyticsService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const mockCheckId = resolvePatMockCheckId(route);
    if (isPatMockMode(mockCheckId) && isPatMockPayAtTableUrl(state.url)) {
      // PatPageComponent will sign in anonymously; skip auth modal for local mock testing
      return of(true);
    }

    // User id / token must be read from signals. `selectUserId` is a toObservable()
    // stream: take(1) can emit a stale `undefined` right after login, so the guard
    // re-opens the auth modal and cancels the redirect to cart.
    return this.authFirebaseService.isAuthenticate$().pipe(
      take(1),
      // Real Firebase session without its Dood user in the store: restore it before deciding,
      // instead of opening the login modal where the user would create a second identity.
      switchMap(firebaseUser =>
        this.authFirebaseService.restoreSessionUser$(firebaseUser).pipe(map(() => firebaseUser)),
      ),
      map(firebaseUser => {
        const hasToken = !!this.authSelector.selectStatusTokenSignal();
        const userId = this.authSelector.selectUser()?.id;
        // Real Firebase session, or Dood guest session (JWT + user, no Firebase).
        const hasRealSession = !!firebaseUser && hasToken && !!userId;
        const hasDoodGuestSession =
          this.authFirebaseService.hasDoodAnonymousSession() && hasToken && !!userId;
        return hasRealSession || hasDoodGuestSession;
      }),
      tap(() => {
        if (!this.store.selectSignal(SELECT_IS_USER_EMAIL_VERIFICATION_PLUGIN_ACTIVE)) {
          return;
        }
        if (
          this.authSelector.selectUser()?.email_verified === false &&
          this.authVerificationEmailService.isUserEmailVerificationPluginActive()
        ) {
          this.modalsService.open('unverified-user-email-alert-modal');
        }
      }),
      map(logged => {
        // Guests have no account profile.
        if (this.authFirebaseService.isAnonymous && state.url === `/${Paths.Account}`) {
          return false;
        }
        const active = this.cartSelector.active;
        const cartPath = state.url.split('?')[0];
        const isCartUrl = cartPath.endsWith(`/${Paths.Cart}`);
        // Full-authentication shops must not checkout as a Dood guest.
        // `endsWith('/cart')` covers locale prefixes (`/fr/cart`); `=== '/cart'` never matches.
        if (
          logged &&
          isCartUrl &&
          this.authFirebaseService.isAnonymous &&
          ![AuthModeValues.Anonymous, AuthModeValues.OnlyAnonymous].includes(
            active?.auth_mode as AuthModeValues,
          )
        ) {
          this.rememberAuthRedirect(state.url);
          this.logCartDecision(state.url, false, 'anonymous_full_auth', logged);
          this.modalsService.open<UntypedModalIO>(AuthenticationModalComponent.handle);
          return false;
        }
        if (!logged) {
          this.rememberAuthRedirect(state.url);
          this.logCartDecision(state.url, false, 'not_logged', logged);
          this.modalsService.open<UntypedModalIO>(AuthenticationModalComponent.handle);
          return false;
        }
        this.logCartDecision(state.url, true, 'allowed', logged);
        return true;
      }),
    );
  }

  /**
   * Keep the first blocked destination (e.g. /fr/cart) when the user browses other
   * routes before finishing Como / email login — otherwise requestedUrl becomes a shop URL.
   */
  private rememberAuthRedirect(url: string): void {
    if (!url) {
      return;
    }
    const current = this.authSelector.selectGuard()?.requestedUrl ?? '';
    const nextIsCart = this.isCartPath(url);
    const currentIsCart = this.isCartPath(current);
    if (currentIsCart && !nextIsCart) {
      return;
    }
    this.authDispatcher.updateGuard({ requestedUrl: url });
  }

  private isCartPath(url: string): boolean {
    return url.split('?')[0].endsWith(`/${Paths.Cart}`);
  }

  private logCartDecision(url: string, allowed: boolean, reason: string, logged: boolean): void {
    if (!url.split('?')[0].endsWith(`/${Paths.Cart}`)) {
      return;
    }
    const user = this.authSelector.selectUser();
    this.analyticsService.log({
      level: allowed ? 'info' : 'warn',
      label: '[DOOD-3204] AuthGuard.cart',
      message: {
        allowed,
        reason,
        logged,
        auth_mode: this.cartSelector.active?.auth_mode ?? null,
        firebase_anonymous: this.authFirebaseService.isAnonymous,
        dood_guest_session: this.authFirebaseService.hasDoodAnonymousSession(),
        user_is_anonymous: user?.is_anonymous ?? null,
        has_user: !!user?.id,
        url,
      },
    });
  }
}
